import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Icon,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { LockClosedIcon } from '@heroicons/react/outline';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  BankIdentityRole,
  CompanyDraftStatus,
  useGetBankIdentitiesByCompanyQuery,
  useGetSpvDraftQuery,
} from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';

import { getDepositBadge } from '../../../utils';
import { SPVMatriculationBankDetailsModal } from './SPVMatriculationBankDetailsModal';

type Props = unknown;

/**
 * SPVMatriculationDeposit.
 * Capital deposit accordion.
 *
 * @returns {JSX.Element}
 */
export function SPVMatriculationDeposit(): JSX.Element {
  const i18nBadge = useTranslations('Badge');
  const i18nSPVDeposit = useTranslations('SPVMatriculationDeposit');

  const bankDetailsModal = useDisclosure();

  const { id = '' } = useParams();

  const spvDraftReq = useGetSpvDraftQuery({ variables: { id } });
  const bankIdentitiesReq = useGetBankIdentitiesByCompanyQuery({
    variables: {
      companyId: spvDraftReq.data?.spvDraft.company.id || '',
    },
    skip: !spvDraftReq.data?.spvDraft.company.id,
  });

  if (spvDraftReq.loading) {
    return <Skeleton h="65px" />;
  }

  if (spvDraftReq.error) {
    return <ErrorQueryCard h="65px" />;
  }

  if (!spvDraftReq.data) {
    return <ErrorQueryCard h="65px" />;
  }

  const draft = spvDraftReq.data.spvDraft;
  const bankIdentities = bankIdentitiesReq.data?.getBankIdentitiesByCompany || [];

  const activeStep = draft?.status === CompanyDraftStatus.VERIFIED ? [0] : [];

  const bankCapitalAccount = bankIdentities.find(
    (bank) => bank.role === BankIdentityRole.SHAREHOLDER,
  )?.bankCapitalAccount;

  const handleIBANMModalOpen = (): void => {
    bankDetailsModal.onOpen();
  };

  const handleIBANMModalClose = (): void => {
    bankDetailsModal.onClose();
  };

  const badgeValues = getDepositBadge(draft, bankCapitalAccount);

  return (
    <>
      <Accordion key={`deposit${draft?.id}`} allowMultiple defaultIndex={activeStep}>
        <AccordionItem isDisabled={!bankCapitalAccount?.iban}>
          <AccordionButton>
            <AccordionIcon boxSize="8" />
            <Box flex="1" p="2" textAlign="left">
              <Text fontSize="xl" fontWeight="semibold">
                {i18nSPVDeposit('Title')}
              </Text>
            </Box>
            <Badge colorScheme={badgeValues.color}>
              {i18nBadge(badgeValues.label as keyof Messages['Badge'])}
            </Badge>
          </AccordionButton>
          <AccordionPanel>
            <Stack spacing="4">
              <Alert status="info">
                <AlertIcon />
                <AlertTitle>{i18nSPVDeposit('InfoBox')}</AlertTitle>
              </Alert>
              <Button
                alignSelf="flex-end"
                leftIcon={<Icon as={LockClosedIcon} />}
                variant="secondary"
                onClick={handleIBANMModalOpen}
              >
                {i18nSPVDeposit('IBANButton')}
              </Button>
            </Stack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      <SPVMatriculationBankDetailsModal
        isOpen={bankDetailsModal.isOpen}
        onClose={handleIBANMModalClose}
      />
    </>
  );
}

export type SPVMatriculationDepositProps = Props;
